import { type FC } from 'react';

import AddIcon from '@mui/icons-material/Add';
import { Box, Button, CircularProgress, Stack, Typography } from '@mui/material';

import { useTrackRecentView } from 'api/user-settings/hooks/useTrackRecentView';
import { RecentViewType } from 'api/user-settings/types';
import { PageHeader } from 'components/layout/page/PageHeader/PageHeader';
import { PageLayout } from 'components/layout/page/PageLayout/PageLayout';
import { NodeDetailsPanel } from 'features/node-details';

import { useDecompositionTreePage } from '../hooks/useDecompositionTreePage';
import { MoveDialogSession } from '../move/components/MoveDialogSession';
import { DECOMPOSITION_TREE_TEST_ID } from '../testIds';
import { ChildrenSubscribers } from './ChildrenSubscribers';
import { DecompositionTree } from './DecompositionTree';
import { InfiniteScrollSentinel } from './InfiniteScrollSentinel';

type RichTreeViewPageProps = {
  projectId: string;
  templateNodeId: string;
  proposalId?: string;
  basePath: string;
};

export const DecompositionTreePage: FC<RichTreeViewPageProps> = ({
  projectId,
  templateNodeId,
  proposalId,
  basePath
}) => {
  const {
    icon,
    name,
    titlePlaceholder,
    isLoading,
    isPending,
    hasMoreRoots,
    isFetchingMoreRoots,
    fetchNextRoots,
    roots,
    treeItems,
    itemMetaMap,
    loadingParentIds,
    openedParents,
    handleSnapshot,
    mainExpandedItems,
    handleExpansionChange,
    handleCollapseAll,
    callbacks,
    sentinelRef,
    moveTarget,
    closeMoveDialog,
    moveNodeMutation,
    addNodeMutation,
    isAddingNode,
    selectedDataNodeId,
    closeDetails,
    dropUnavailableDetails,
    templateNode
  } = useDecompositionTreePage({ projectId, templateNodeId, proposalId });

  // Every entry point that links here calls this page "{name} hierarchy" (see
  // `state/selectedProject/useOverviews.ts`'s `${nodeDisplayName} hierarchy` link name) — match that in
  // the title/breadcrumb rather than the bare node-type name.
  const hierarchyName = `${name} hierarchy`;

  // `name` reads "Unknown" until the blueprint has loaded the node, so wait for the node itself.
  useTrackRecentView({
    projectId,
    viewType: RecentViewType.Hierarchy,
    targetId: templateNodeId,
    name: templateNode ? hierarchyName : undefined,
    templateNodeId
  });

  return (
    <PageLayout
      id='decomposition-tree-page'
      contentOverflow='hidden'
      disableContentPadding={['top', 'sides', 'bottom']}
      header={
        <PageHeader
          data-testid='decomposition-tree-page-header'
          breadcrumbs={[{ title: 'Views', link: `${basePath}/data` }, { title: hierarchyName }]}
          title={hierarchyName}
          actions={
            <Button
              variant='contained'
              size='small'
              startIcon={<AddIcon />}
              onClick={() => addNodeMutation()}
              disabled={!templateNode || isLoading || isPending || isAddingNode}
              loading={isAddingNode}
              data-testid={DECOMPOSITION_TREE_TEST_ID.addNodeButton}
            >
              Create {name}
            </Button>
          }
        />
      }
    >
      <Stack
        direction='row'
        data-testid={DECOMPOSITION_TREE_TEST_ID.page}
        sx={{
          height: 1,
          width: 1,
          minHeight: 0,
          backgroundColor: 'background.paper'
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            minWidth: 0,
            height: '100%',
            minHeight: 0,
            p: 3
          }}
        >
          {treeItems.length > 0 && (
            <Button
              size='small'
              onClick={handleCollapseAll}
              data-testid={DECOMPOSITION_TREE_TEST_ID.collapseAllButton}
              sx={{ mb: 1, alignSelf: 'flex-start' }}
            >
              Collapse All
            </Button>
          )}

          {isLoading || isPending ? (
            <Stack alignItems='center' justifyContent='center' sx={{ p: 4, flex: 1, minHeight: 200 }}>
              <CircularProgress size={32} />
            </Stack>
          ) : (
            <Stack
              spacing={1}
              sx={{
                px: 2,
                flex: 1,
                width: '100%',
                minHeight: 0,
                overflow: 'auto'
              }}
            >
              <ChildrenSubscribers
                openedParents={openedParents}
                projectId={projectId}
                templateNodeId={templateNodeId}
                proposalId={proposalId}
                titlePlaceholder={titlePlaceholder}
                onSnapshot={handleSnapshot}
              />

              {treeItems.length === 0 && <Typography variant='body1'>No data available</Typography>}

              {treeItems.length > 0 && (
                <Box sx={{ maxWidth: { xs: '100%', md: '600px' } }}>
                  <DecompositionTree
                    items={treeItems}
                    itemMetaMap={itemMetaMap}
                    callbacks={callbacks}
                    expandedItems={mainExpandedItems}
                    onExpandedItemsChange={handleExpansionChange}
                    nodeIcon={icon}
                    nodeName={name}
                    loadingParentIds={loadingParentIds}
                  />
                </Box>
              )}

              <InfiniteScrollSentinel ref={sentinelRef} active={!!hasMoreRoots} isFetching={isFetchingMoreRoots} />

              {moveTarget && (
                <MoveDialogSession
                  key={moveTarget.moveNodeId}
                  target={moveTarget}
                  onClose={closeMoveDialog}
                  projectId={projectId}
                  templateNodeId={templateNodeId}
                  proposalId={proposalId}
                  roots={roots}
                  titlePlaceholder={titlePlaceholder}
                  moveNodeMutation={moveNodeMutation}
                  nodeIcon={icon}
                  hasMoreRoots={hasMoreRoots}
                  isFetchingMoreRoots={isFetchingMoreRoots}
                  onLoadMoreRoots={fetchNextRoots}
                />
              )}
            </Stack>
          )}
        </Box>

        {selectedDataNodeId && (
          <NodeDetailsPanel
            projectId={projectId}
            templateNodeId={templateNodeId}
            dataNodeId={selectedDataNodeId}
            proposalId={proposalId}
            onClose={closeDetails}
            onUnavailable={dropUnavailableDetails}
          />
        )}
      </Stack>
    </PageLayout>
  );
};
